/**
 * StepDebugPanel
 *
 * Terminal-style execution log viewer for the step-by-step pipeline.
 * Shows per-step logs collected by StepRunner during execution.
 * Auto-selects the currently running step and auto-scrolls to the latest entry.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createCustomerETLApi } from '../api/etlServiceApi';

export const STEP_DEFS = [
  { id: 'extract',       num: '1', title: 'Extract',       bgSelected: 'bg-blue-500',    bgDot: 'bg-blue-400' },
  { id: 'build_objects', num: '2', title: 'Build Objects', bgSelected: 'bg-indigo-500',  bgDot: 'bg-indigo-400' },
  { id: 'build_events',  num: '3', title: 'Build Events',  bgSelected: 'bg-violet-500',  bgDot: 'bg-violet-400' },
  { id: 'merge_map',     num: '4', title: 'Merge & Map',   bgSelected: 'bg-amber-500',   bgDot: 'bg-amber-400' },
  { id: 'assemble',      num: '5', title: 'Assemble',      bgSelected: 'bg-emerald-500', bgDot: 'bg-emerald-400' },
];

export default function StepDebugPanel({ steps, customerId }) {
  const [selectedStepId, setSelectedStepId] = useState('extract');
  const logEndRef = useRef(null);
  const userScrolledRef = useRef(false);
  const containerRef = useRef(null);

  const api = useMemo(() => (customerId ? createCustomerETLApi(customerId) : null), [customerId]);

  // Past-run history for the selected step — null viewingRunId means "live"
  const [history, setHistory] = useState([]);
  const [viewingRunId, setViewingRunId] = useState(null);
  const [viewingRun, setViewingRun] = useState(null);
  const [viewingRunLoading, setViewingRunLoading] = useState(false);

  // Auto-select whichever step is currently running
  useEffect(() => {
    const runningStep = steps.find((s) => s.status === 'running');
    if (runningStep) {
      setSelectedStepId(runningStep.step_id);
      userScrolledRef.current = false; // reset scroll when step changes
    }
  }, [steps]);

  // Load run history whenever the selected step changes, and drop back to "live"
  useEffect(() => {
    setViewingRunId(null);
    setViewingRun(null);
    if (!api) { setHistory([]); return; }
    api.getStepHistory(selectedStepId, 20)
      .then(setHistory)
      .catch(() => setHistory([]));
  }, [selectedStepId, api]);

  const handleSelectRun = async (runId) => {
    if (!runId) {
      setViewingRunId(null);
      setViewingRun(null);
      return;
    }
    setViewingRunId(runId);
    setViewingRunLoading(true);
    userScrolledRef.current = false;
    try {
      const run = await api.pollStepRun(runId);
      setViewingRun(run);
    } catch {
      setViewingRun(null);
    } finally {
      setViewingRunLoading(false);
    }
  };

  const selectedStep = steps.find((s) => s.step_id === selectedStepId);
  const isViewingPastRun = !!viewingRunId;
  const logs = isViewingPastRun ? (viewingRun?.logs ?? []) : (selectedStep?.logs ?? []);

  // Auto-scroll to bottom when logs grow (unless user has scrolled up)
  useEffect(() => {
    if (!userScrolledRef.current && logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs.length]);

  const handleScroll = (e) => {
    const el = e.currentTarget;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    userScrolledRef.current = distFromBottom > 40;
  };

  const handleStepSelect = (stepId) => {
    setSelectedStepId(stepId);
    setViewingRunId(null);
    setViewingRun(null);
    userScrolledRef.current = false;
  };

  const selectedServerStep = steps.find((s) => s.step_id === selectedStepId);
  const selectedStatus = isViewingPastRun ? (viewingRun?.status ?? 'pending') : (selectedServerStep?.status ?? 'pending');

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m6.75 7.5 3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0 0 21 18V6a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6v12a2.25 2.25 0 0 0 2.25 2.25Z" />
          </svg>
          <h3 className="text-sm font-semibold text-gray-700">Step Logs</h3>
        </div>
        <div className="flex items-center gap-2">
          {history.length > 0 && (
            <select
              value={viewingRunId ?? ''}
              onChange={(e) => handleSelectRun(e.target.value || null)}
              className="text-[11px] text-gray-500 border border-gray-200 rounded-md px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-400"
              title="View logs from a past run of this step"
            >
              <option value="">Latest (live)</option>
              {history.map((run) => (
                <option key={run.step_run_id} value={run.step_run_id}>
                  {run.started_at ? new Date(run.started_at).toLocaleString() : run.step_run_id} — {run.status}
                </option>
              ))}
            </select>
          )}
          {!isViewingPastRun && selectedStatus === 'running' && (
            <span className="flex items-center gap-1 text-[10px] text-blue-600">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
              Live
            </span>
          )}
          <span className="text-[11px] text-gray-400">
            {viewingRunLoading ? 'Loading…' : `${logs.length} line${logs.length !== 1 ? 's' : ''}`}
          </span>
        </div>
      </div>

      {/* Step selector tabs */}
      <div className="px-3 py-2 border-b border-gray-100 flex gap-1.5 flex-wrap bg-gray-50">
        {STEP_DEFS.map((stepDef) => {
          const serverStep = steps.find((s) => s.step_id === stepDef.id);
          const isRunning = serverStep?.status === 'running';
          const isDone = serverStep?.status === 'done';
          const isFailed = serverStep?.status === 'failed';
          const isSelected = selectedStepId === stepDef.id;

          return (
            <button
              key={stepDef.id}
              onClick={() => handleStepSelect(stepDef.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors ${
                isSelected
                  ? `${stepDef.bgSelected} text-white shadow-sm`
                  : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-100'
              }`}
            >
              {isRunning && (
                <span
                  className={`w-1.5 h-1.5 rounded-full animate-pulse ${
                    isSelected ? 'bg-white' : stepDef.bgDot
                  }`}
                />
              )}
              {isDone && !isSelected && (
                <svg className="w-3 h-3 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
              )}
              {isFailed && !isSelected && (
                <svg className="w-3 h-3 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              )}
              {stepDef.num}. {stepDef.title}
            </button>
          );
        })}
      </div>

      {/* Terminal log area */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="bg-gray-950 font-mono text-[11px] leading-5 overflow-y-auto p-3"
        style={{ height: '220px' }}
      >
        {logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-600 gap-1">
            <svg className="w-5 h-5 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m6.75 7.5 3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0 0 21 18V6a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6v12a2.25 2.25 0 0 0 2.25 2.25Z" />
            </svg>
            <span className="text-[11px]">No logs yet — run this step to see execution details.</span>
          </div>
        ) : (
          <>
            {logs.map((line, i) => {
              const isError = line.includes('ERROR');
              const isWarning = !isError && (line.includes('WARNING') || line.includes('WARN'));
              const levelClass = isError ? 'text-red-400' : isWarning ? 'text-amber-400' : 'text-gray-300';
              const isTimestamp = line.startsWith('[');
              const timestampMatch = isTimestamp ? line.match(/^\[(\d{2}:\d{2}:\d{2})\] (.*)$/) : null;

              if (timestampMatch) {
                return (
                  <div key={i} className="flex gap-2 py-0.5">
                    <span className="text-gray-600 flex-shrink-0 select-none">{timestampMatch[1]}</span>
                    <span className={`${levelClass} whitespace-pre-wrap break-all`}>
                      {timestampMatch[2]}
                    </span>
                  </div>
                );
              }
              return (
                <div key={i} className={`py-0.5 whitespace-pre-wrap break-all ${levelClass}`}>
                  {line}
                </div>
              );
            })}
          </>
        )}
        <div ref={logEndRef} />
      </div>

      {/* Scroll-to-bottom hint when user has scrolled up */}
      {userScrolledRef.current && (
        <div className="absolute bottom-0 right-0 p-2">
          <button
            onClick={() => {
              userScrolledRef.current = false;
              logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="text-[10px] bg-gray-800 text-gray-300 px-2 py-1 rounded hover:bg-gray-700 transition-colors"
          >
            ↓ Latest
          </button>
        </div>
      )}
    </div>
  );
}
